"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React, { useEffect } from "react"

import { PHONE_EMAIL_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"

import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"

const PhoneEmailStep: React.FC<StepComponentProps> = ({ onComplete, progressPercentage, onBack, stepId }) => {
  const phoneEmailText = PHONE_EMAIL_STEP_TEXTS

  const { phoneNumber, email, sendVerificationCode } = useSignup()

  useEffect(() => {
    if (!phoneNumber.value) {
      phoneNumber.setValue("+52")
    }
  }, [phoneNumber])

  const formatPhoneNumber = (input: string): string => {
    if (!input.startsWith("+")) {
      input = "+52" + input.replace(/^\+?(52|1)/, "")
    }
    return input.replace(/(?!^\+)\D/g, "")
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (phoneNumber.value) {
      phoneNumber.setValue(formatPhoneNumber(phoneNumber.value))
    }

    phoneNumber.setTouched(true)
    phoneNumber.setBlurred(true)
    email.setTouched(true)
    email.setBlurred(true)
    if (!phoneNumber.isValid || !email.isValid) {
      return
    }

    try {
      await sendVerificationCode.execute()
      onComplete()
    } catch (error) {
      console.error("Error sending verification code:", error)
      datadogRum.addError(error, {
        step: stepId,
        action: "sendVerificationCode",
        errorMessage: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  return (
    <StepWrapper
      title={phoneEmailText.phoneEmail.title}
      subtitle={phoneEmailText.phoneEmail.subtitle}
      progressPercentage={progressPercentage}
      isLoading={sendVerificationCode.isLoading}
      formError={sendVerificationCode.error?.message}
      onSubmit={handleSubmit}
      submitDisabled={!phoneNumber.isValid || !email.isValid || sendVerificationCode.isLoading}
      submitButtonText={phoneEmailText.phoneEmail.buttonText}
      footerText={phoneEmailText.phoneEmail.footerText}
      showBackButton={!!onBack}
      onBack={onBack}
    >
      <div>
        <TextInput
          label={phoneEmailText.labels.mobilePhone}
          type="tel"
          value={phoneNumber.value}
          onChange={(e) => {
            let value = e.target.value
            if (value && !value.startsWith("+")) {
              value = `+${value}`
            }

            try {
              if (value.replace(/\D/g, "").length > 5) {
                phoneNumber.setValue(formatPhoneNumber(value))
              } else {
                phoneNumber.setValue(value)
              }
            } catch (error) {
              phoneNumber.setValue(value)
            }
          }}
          onBlur={() => {
            phoneNumber.setTouched(true)
            phoneNumber.setBlurred(true)
            if (phoneNumber.errorMessage) {
              datadogRum.addError(new Error(`Phone validation error: ${phoneNumber.errorMessage}`), {
                step: stepId,
                field: "phoneNumber",
              })
            }
          }}
          onFocus={() => phoneNumber.setTouched(true)}
          isValid={phoneNumber.isValid}
          touched={phoneNumber.touched}
          blurred={phoneNumber.blurred}
          errorMessage={phoneNumber.errorMessage}
        />

        <TextInput
          label={phoneEmailText.labels.email}
          type="email"
          value={email.value}
          onChange={(e) => email.setValue(e.target.value)}
          onBlur={() => {
            email.setTouched(true)
            email.setBlurred(true)
            if (email.errorMessage) {
              datadogRum.addError(new Error(`Email validation error: ${email.errorMessage}`), {
                step: stepId,
                field: "email",
              })
            }
          }}
          onFocus={() => email.setTouched(true)}
          isValid={email.isValid}
          touched={email.touched}
          blurred={email.blurred}
          errorMessage={email.errorMessage}
        />
      </div>
    </StepWrapper>
  )
}

export default PhoneEmailStep
